import { memo, useCallback, useState } from 'react'
import { Pressable, StyleSheet, useWindowDimensions, View } from 'react-native'
import {
    Button,
    Checkbox,
    Divider,
    Icon,
    IconButton,
    Text,
    Tooltip,
    useTheme,
} from 'react-native-paper'
import { useSemanticColors } from '@/constants/SemanticColors'
import { useClockOut } from '@/hooks/useClockOut'
import { useDailyActivityCard } from '@/hooks/useDailyActivityCard'
import { useLiveElapsed } from '@/hooks/useLiveElapsed'
import { BREAKPOINTS } from '@/lib/config'
import {
    formatHoursReadable,
    formatTimeOfDay,
    getNetWorkSeconds,
    secondsToHours,
} from '@/lib/dates'
import type { DayGroup } from '@/lib/group-entries-by-date'
import { displayLocationName } from '@/lib/location-utils'
import { isTravelRouteNotes } from '@/lib/movement-utils'
import { useConsolidationSelection } from '@/stores/consolidation-store'
import type { TimeEntriesExpanded } from '@/types/timeTracking'
import { ActiveTimerEditSheet } from './ActiveTimerEditSheet'
import { AdminEditedChip } from './AdminEditedChip'
import { EntryTypeChip } from './EntryTypeChip'
import { ManualEntryInfoDialog } from './ManualEntryInfoDialog'
import { StatusDot } from './StatusDot'

interface DailyActivityCardProps {
    dayGroup: DayGroup
    onOpenRejectDialog?: (entry: TimeEntriesExpanded) => void
    onEditEntry: (entry: TimeEntriesExpanded) => void
    onTravelTap?: (entry: TimeEntriesExpanded) => void
    isAdmin: boolean
    showApprove?: boolean
    showEdit?: boolean
    onViewHistory?: (entry: TimeEntriesExpanded) => void
    onAddEntry?: () => void
    userName?: string
    userColor?: string | null
    expanded?: boolean
    onToggleExpanded?: () => void
    isEntryLocked?: (entry: TimeEntriesExpanded) => boolean
    showConsolidate?: boolean
    onConsolidate?: (groupKey: string) => void
}

export const DailyActivityCard = memo(function DailyActivityCard({
    dayGroup,
    onOpenRejectDialog,
    onEditEntry,
    onTravelTap,
    isAdmin,
    showApprove = true,
    showEdit,
    onViewHistory,
    onAddEntry,
    userName,
    userColor,
    expanded = true,
    onToggleExpanded,
    isEntryLocked,
    showConsolidate,
    onConsolidate,
}: DailyActivityCardProps) {
    const orgId = dayGroup.entries[0]?.org
    const userId = dayGroup.entries[0]?.user
    const groupKey = `${userId}:${dayGroup.dateKey}`
    const pendingEntryIds = dayGroup.entries.filter(e => e.status === 'pending').map(e => e.id)
    const { approveDay, approveEntry, isApproving } = useDailyActivityCard(pendingEntryIds, orgId)
    const hasPendingEntries = pendingEntryIds.length > 0
    const { canConsolidate } = useConsolidationSelection(groupKey)

    return (
        // E2E: tests scope approval/merge actions to a specific user's card via
        // this testID (keyed by user id, which is stable in both Day and User
        // grouping; see approval-flow / merge-entries specs). Do not remove.
        <View style={styles.dayGroup} testID={userId ? `activity-card-${userId}` : undefined}>
            <DailyCardHeader
                dayGroup={dayGroup}
                userName={userName}
                userColor={userColor}
                onAddEntry={onAddEntry}
                expanded={expanded}
                onToggle={onToggleExpanded}
                canConsolidate={showConsolidate && canConsolidate}
                onConsolidate={onConsolidate ? () => onConsolidate(groupKey) : undefined}
            />
            {expanded && (
                <>
                    <EntryList
                        dayGroup={dayGroup}
                        onOpenRejectDialog={onOpenRejectDialog}
                        onEditEntry={onEditEntry}
                        onTravelTap={onTravelTap}
                        isAdmin={isAdmin}
                        showEdit={showEdit}
                        showClockOut={showApprove}
                        onViewHistory={onViewHistory}
                        approveEntry={approveEntry}
                        isEntryLocked={isEntryLocked}
                        showConsolidate={showConsolidate}
                        groupKey={groupKey}
                    />
                    <DailyCardFooter
                        dayGroup={dayGroup}
                        onApproveDay={approveDay}
                        isApproving={isApproving}
                        showApprove={showApprove && hasPendingEntries}
                    />
                </>
            )}
        </View>
    )
})

interface EntryListProps {
    dayGroup: DayGroup
    onOpenRejectDialog?: (entry: TimeEntriesExpanded) => void
    onEditEntry: (entry: TimeEntriesExpanded) => void
    onTravelTap?: (entry: TimeEntriesExpanded) => void
    isAdmin: boolean
    showEdit?: boolean
    showClockOut?: boolean
    onViewHistory?: (entry: TimeEntriesExpanded) => void
    approveEntry: (entryId: string) => void
    isEntryLocked?: (entry: TimeEntriesExpanded) => boolean
    showConsolidate?: boolean
    groupKey?: string
}

function EntryList(props: EntryListProps) {
    const semanticColors = useSemanticColors()
    const { width } = useWindowDimensions()
    const isDesktop = width >= BREAKPOINTS.lg
    const { checkedIds, toggleEntry } = useConsolidationSelection(props.groupKey ?? '')
    const isSelectable = !!props.showConsolidate
    const [manualInfoEntry, setManualInfoEntry] = useState<TimeEntriesExpanded | null>(null)
    const onShowEntryInfo = useCallback(
        (entry: TimeEntriesExpanded) => setManualInfoEntry(entry),
        []
    )

    return (
        <>
            {props.dayGroup.entries.map((entry, index) => (
                <View
                    key={entry.id}
                    style={{
                        borderLeftWidth: 3,
                        borderLeftColor:
                            entry.status === 'pending'
                                ? semanticColors.statusPending
                                : 'transparent',
                    }}
                >
                    {index > 0 && <Divider />}
                    <EntryRow
                        entry={entry}
                        {...props}
                        isDesktop={isDesktop}
                        onShowEntryInfo={onShowEntryInfo}
                        isSelectable={isSelectable && entry.status !== 'active'}
                        isSelected={checkedIds.has(entry.id)}
                        onToggleSelect={() => toggleEntry(entry.id, props.dayGroup.entryIds)}
                    />
                </View>
            ))}
            <ManualEntryInfoDialog
                visible={!!manualInfoEntry}
                entry={manualInfoEntry}
                onDismiss={() => setManualInfoEntry(null)}
            />
        </>
    )
}

interface EntryRowProps extends EntryListProps {
    entry: TimeEntriesExpanded
    isDesktop: boolean
    onShowEntryInfo: (entry: TimeEntriesExpanded) => void
    isSelectable?: boolean
    isSelected?: boolean
    onToggleSelect?: () => void
}

function EntryRow({
    entry,
    dayGroup,
    onOpenRejectDialog,
    onEditEntry,
    onTravelTap,
    isAdmin,
    showEdit,
    showClockOut,
    onViewHistory,
    approveEntry,
    isEntryLocked,
    isDesktop,
    onShowEntryInfo,
    isSelectable,
    isSelected,
    onToggleSelect,
    showConsolidate,
}: EntryRowProps) {
    const locked = isEntryLocked?.(entry) ?? false
    const overlapMinutes = dayGroup.overlappingEntryIds.get(entry.id)
    const travelTap = onTravelTap ? () => onTravelTap(entry) : undefined

    if (entry.status === 'active') {
        const ActiveRow = isDesktop ? ActiveDesktopEntryRow : ActiveMobileEntryRow
        return (
            <ActiveRow
                entry={entry}
                onTravelTap={travelTap}
                overlapMinutes={overlapMinutes}
                isLocked={locked}
                showClockOut={showClockOut}
                isAdmin={isAdmin}
                onShowEntryInfo={onShowEntryInfo}
                showCheckboxSpacer={!!showConsolidate}
            />
        )
    }

    const Row = isDesktop ? DesktopEntryRow : MobileEntryRow
    return (
        <Row
            entry={entry}
            onReject={onOpenRejectDialog ? () => onOpenRejectDialog(entry) : undefined}
            onApprove={
                isAdmin && entry.status === 'rejected' ? () => approveEntry(entry.id) : undefined
            }
            onEdit={() => onEditEntry(entry)}
            onTravelTap={travelTap}
            isAdmin={isAdmin}
            showEdit={showEdit}
            onViewHistory={onViewHistory}
            overlapMinutes={overlapMinutes}
            isLocked={locked}
            onShowEntryInfo={onShowEntryInfo}
            isSelectable={isSelectable}
            isSelected={isSelected}
            onToggleSelect={onToggleSelect}
        />
    )
}

interface DailyCardHeaderProps {
    dayGroup: DayGroup
    userName?: string
    userColor?: string | null
    onAddEntry?: () => void
    expanded?: boolean
    onToggle?: () => void
    canConsolidate?: boolean
    onConsolidate?: () => void
}

function DailyCardHeader({
    dayGroup,
    userName,
    userColor,
    onAddEntry,
    expanded,
    onToggle,
    canConsolidate,
    onConsolidate,
}: DailyCardHeaderProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const isCollapsible = onToggle != null
    const pendingCount = dayGroup.entries.filter(e => e.status === 'pending').length

    const headerContent = (
        <View style={[styles.header, { borderBottomColor: theme.colors.outline }]}>
            <View style={styles.headerLeft}>
                {userName && (
                    <View style={styles.userRow}>
                        <Icon
                            source="account"
                            size={16}
                            color={userColor || theme.colors.onSurfaceVariant}
                        />
                        <Text
                            variant="titleMedium"
                            style={{ fontWeight: '600', color: theme.colors.onSurface }}
                        >
                            {userName}
                        </Text>
                    </View>
                )}
                {!userName && (
                    <View style={styles.dateLabelRow}>
                        <Text
                            variant="titleMedium"
                            style={{ fontWeight: '600', color: theme.colors.onSurface }}
                        >
                            {dayGroup.dateLabel}
                        </Text>
                        {dayGroup.hasOverlap && (
                            <Tooltip title="This day has overlapping time entries">
                                <View>
                                    <Icon
                                        source="alert-circle-outline"
                                        size={16}
                                        color={semanticColors.warning}
                                    />
                                </View>
                            </Tooltip>
                        )}
                    </View>
                )}
            </View>
            <View style={styles.headerRight}>
                {pendingCount > 0 && (
                    <Text variant="bodySmall" style={{ color: semanticColors.statusPending }}>
                        {pendingCount} unreviewed
                    </Text>
                )}
                <Text
                    variant="bodySmall"
                    style={{ color: theme.colors.primary, fontWeight: '600' }}
                >
                    {formatHoursReadable(dayGroup.totalWorkHours)}
                </Text>
                {onConsolidate && (
                    <Tooltip title="Merge selected entries">
                        <IconButton
                            icon="call-merge"
                            size={18}
                            onPress={onConsolidate}
                            disabled={!canConsolidate}
                            style={styles.iconBtn}
                        />
                    </Tooltip>
                )}
                {onAddEntry && (
                    <Tooltip title="Add entry">
                        <IconButton
                            icon="plus-circle"
                            size={18}
                            onPress={onAddEntry}
                            style={styles.iconBtn}
                        />
                    </Tooltip>
                )}
                {isCollapsible && (
                    <Icon
                        source={expanded ? 'chevron-up' : 'chevron-down'}
                        size={24}
                        color={theme.colors.onSurfaceVariant}
                    />
                )}
            </View>
        </View>
    )

    if (isCollapsible) {
        return <Pressable onPress={onToggle}>{headerContent}</Pressable>
    }

    return headerContent
}

interface DailyCardFooterProps {
    dayGroup: DayGroup
    onApproveDay: () => void
    isApproving: boolean
    showApprove: boolean
}

function DailyCardFooter({
    dayGroup,
    onApproveDay,
    isApproving,
    showApprove,
}: DailyCardFooterProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()

    return (
        <View style={[styles.footer, { borderTopColor: theme.colors.outline }]}>
            <View style={styles.footerTotal}>
                <Text variant="labelSmall" style={{ color: theme.colors.onSurfaceVariant }}>
                    Total
                </Text>
                <Text
                    variant="titleMedium"
                    style={{ fontWeight: 'bold', color: theme.colors.primary }}
                >
                    {formatHoursReadable(dayGroup.totalWorkHours)}
                </Text>
            </View>
            {showApprove && (
                <Button
                    mode="contained"
                    onPress={onApproveDay}
                    loading={isApproving}
                    disabled={isApproving}
                    icon="check-all"
                    buttonColor={semanticColors.success}
                    compact
                    labelStyle={styles.approveLabel}
                >
                    Approve Pending
                </Button>
            )}
        </View>
    )
}

interface CompactEntryRowProps {
    entry: TimeEntriesExpanded
    onReject?: () => void
    onApprove?: () => void
    onEdit: () => void
    onTravelTap?: () => void
    isAdmin: boolean
    showEdit?: boolean
    onViewHistory?: (entry: TimeEntriesExpanded) => void
    overlapMinutes: number | undefined
    isLocked?: boolean
    onShowEntryInfo: (entry: TimeEntriesExpanded) => void
    isSelectable?: boolean
    isSelected?: boolean
    onToggleSelect?: () => void
}

function formatOverlapTooltip(minutes: number): string {
    const h = Math.floor(minutes / 60)
    const m = minutes % 60
    const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'}`
    if (h === 0) return `Starts ${plural(m, 'minute')} before previous entry ended`
    if (m === 0) return `Starts ${plural(h, 'hour')} before previous entry ended`
    return `Starts ${plural(h, 'hour')} ${plural(m, 'minute')} before previous entry ended`
}

function getEntryDetailText(entry: TimeEntriesExpanded): {
    detailText: string
    remainingNotes: string
} {
    if (entry.entry_type === 'travel' && isTravelRouteNotes(entry.notes)) {
        const [firstLine, ...rest] = (entry.notes ?? '').split('\n')
        return { detailText: firstLine, remainingNotes: rest.join('\n').trim() }
    }
    const detailParts = [
        entry.expand?.job?.name,
        displayLocationName(entry.expand?.location?.name),
    ].filter(Boolean)
    return { detailText: detailParts.join(' · '), remainingNotes: entry.notes || '' }
}

function getEntryTypeChipPress(
    entry: TimeEntriesExpanded,
    isAdmin: boolean,
    onTravelTap?: () => void,
    onShowInfo?: () => void
): (() => void) | undefined {
    if (entry.entry_type === 'travel') return onTravelTap
    if (
        (entry.entry_type === 'manual' ||
            entry.entry_type === 'timer' ||
            entry.entry_type === 'geofence') &&
        isAdmin
    ) {
        return onShowInfo
    }
    return undefined
}

function useEntryDisplayData(entry: TimeEntriesExpanded) {
    const workHours = secondsToHours(getNetWorkSeconds(entry))
    const wasEdited = !!entry.modified_by
    const clockOut = entry.clock_out ? formatTimeOfDay(entry.clock_out) : 'active'
    const timeRange = `${formatTimeOfDay(entry.clock_in)}–${clockOut}`
    const { detailText, remainingNotes } = getEntryDetailText(entry)
    const isRejected = entry.status === 'rejected'
    return { workHours, wasEdited, isRejected, timeRange, detailText, remainingNotes }
}

interface ActiveCompactEntryRowProps {
    entry: TimeEntriesExpanded
    onTravelTap?: () => void
    overlapMinutes: number | undefined
    isLocked?: boolean
    showClockOut?: boolean
    isAdmin: boolean
    onShowEntryInfo: (entry: TimeEntriesExpanded) => void
    showCheckboxSpacer?: boolean
}

function useActiveEntryData(entry: TimeEntriesExpanded) {
    const elapsed = useLiveElapsed(entry.clock_in)
    const clockOut = useClockOut()
    const elapsedHours = elapsed / 3600
    const { detailText } = getEntryDetailText(entry)

    const handleStop = () => {
        clockOut.mutate({ entryId: entry.id, clockIn: entry.clock_in })
    }

    return { elapsedHours, detailText, handleStop, isPending: clockOut.isPending }
}

function ActiveDesktopEntryRow({
    entry,
    onTravelTap,
    overlapMinutes,
    isLocked,
    showClockOut = true,
    isAdmin,
    onShowEntryInfo,
    showCheckboxSpacer,
}: ActiveCompactEntryRowProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const { elapsedHours, detailText, handleStop, isPending } = useActiveEntryData(entry)
    const [showEditor, setShowEditor] = useState(false)
    const chipPress = getEntryTypeChipPress(entry, isAdmin, onTravelTap, () =>
        onShowEntryInfo(entry)
    )

    return (
        <>
            <View style={styles.entryRowDesktop}>
                {showCheckboxSpacer && <View style={styles.checkboxSpacer} />}
                <StatusDot status="active" />
                <Text
                    variant="bodySmall"
                    style={[styles.timeRange, { color: theme.colors.onSurfaceVariant }]}
                >
                    {formatTimeOfDay(entry.clock_in)}–…
                </Text>
                <View style={styles.entryTypeColumn}>
                    <EntryTypeChip type={entry.entry_type} onPress={chipPress} />
                </View>
                <Text
                    variant="bodyMedium"
                    style={[styles.detailText, { color: theme.colors.onSurface }]}
                    numberOfLines={1}
                >
                    {detailText}
                </Text>
                {isLocked && (
                    <Tooltip title="This period is locked — contact your admin">
                        <View>
                            <Icon source="lock" size={16} color={theme.colors.onSurfaceVariant} />
                        </View>
                    </Tooltip>
                )}
                {overlapMinutes != null && (
                    <Tooltip title={formatOverlapTooltip(overlapMinutes)}>
                        <View>
                            <Icon
                                source="swap-horizontal"
                                size={16}
                                color={semanticColors.warning}
                            />
                        </View>
                    </Tooltip>
                )}
                <Text
                    variant="bodyMedium"
                    style={{
                        fontWeight: '600',
                        color: theme.colors.primary,
                        minWidth: 44,
                        textAlign: 'right',
                    }}
                >
                    {formatHoursReadable(elapsedHours)}
                </Text>
                {!isLocked && (
                    <View style={styles.entryActions}>
                        <IconButton
                            icon="pencil"
                            size={18}
                            onPress={() => setShowEditor(true)}
                            style={styles.iconBtn}
                        />
                        {showClockOut && (
                            <IconButton
                                icon="timer-stop"
                                size={18}
                                onPress={handleStop}
                                disabled={isPending}
                                loading={isPending}
                                iconColor={semanticColors.error}
                                style={styles.iconBtn}
                            />
                        )}
                    </View>
                )}
            </View>
            <ActiveTimerEditSheet
                visible={showEditor}
                entry={entry}
                onDismiss={() => setShowEditor(false)}
            />
        </>
    )
}

function ActiveMobileEntryRow({
    entry,
    onTravelTap,
    overlapMinutes,
    isLocked,
    showClockOut = true,
    isAdmin,
    onShowEntryInfo,
    showCheckboxSpacer,
}: ActiveCompactEntryRowProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const { elapsedHours, detailText, handleStop, isPending } = useActiveEntryData(entry)
    const [showEditor, setShowEditor] = useState(false)
    const chipPress = getEntryTypeChipPress(entry, isAdmin, onTravelTap, () =>
        onShowEntryInfo(entry)
    )

    return (
        <>
            <View style={styles.entryRowMobile}>
                <View style={styles.mobileTopRow}>
                    {showCheckboxSpacer && <View style={styles.checkboxSpacer} />}
                    <StatusDot status="active" />
                    <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                        {formatTimeOfDay(entry.clock_in)}–…
                    </Text>
                    <View style={{ flex: 1 }} />
                    <Text
                        variant="bodyMedium"
                        style={{ fontWeight: '600', color: theme.colors.primary }}
                    >
                        {formatHoursReadable(elapsedHours)}
                    </Text>
                </View>
                <View style={styles.mobileDetailRow}>
                    <EntryTypeChip type={entry.entry_type} onPress={chipPress} iconOnly />
                    {detailText ? (
                        <Text
                            variant="bodySmall"
                            style={{ color: theme.colors.onSurfaceVariant, flex: 1 }}
                            numberOfLines={1}
                        >
                            {detailText}
                        </Text>
                    ) : (
                        <View style={{ flex: 1 }} />
                    )}
                    {isLocked && (
                        <Icon source="lock" size={14} color={theme.colors.onSurfaceVariant} />
                    )}
                    {overlapMinutes != null && (
                        <Icon source="swap-horizontal" size={14} color={semanticColors.warning} />
                    )}
                    {!isLocked && (
                        <IconButton
                            icon="pencil"
                            size={18}
                            onPress={() => setShowEditor(true)}
                            style={styles.iconBtn}
                        />
                    )}
                    {showClockOut && !isLocked && (
                        <IconButton
                            icon="timer-stop"
                            size={18}
                            onPress={handleStop}
                            disabled={isPending}
                            loading={isPending}
                            iconColor={semanticColors.error}
                            style={styles.iconBtn}
                        />
                    )}
                </View>
            </View>
            <ActiveTimerEditSheet
                visible={showEditor}
                entry={entry}
                onDismiss={() => setShowEditor(false)}
            />
        </>
    )
}

function shouldShowEditButton(
    entry: TimeEntriesExpanded,
    isAdmin: boolean,
    showEdit?: boolean,
    isLocked?: boolean
) {
    if (isAdmin) return true
    if (isLocked) return false
    if (!showEdit) return false
    return entry.status === 'pending'
}

function shouldShowHistoryButton(
    entry: TimeEntriesExpanded,
    onViewHistory?: (entry: TimeEntriesExpanded) => void
) {
    if (!onViewHistory) return false
    return entry.modified_by && entry.modified_by !== entry.user
}

function RejectionReasonRow({ entry }: { entry: TimeEntriesExpanded }) {
    const semanticColors = useSemanticColors()

    if (entry.status !== 'rejected' || !entry.rejection_reason) return null

    return (
        <View style={styles.rejectionRow}>
            <Icon source="alert-outline" size={14} color={semanticColors.error} />
            <Text
                variant="bodySmall"
                style={{ color: semanticColors.error, fontStyle: 'italic', flex: 1 }}
                numberOfLines={2}
            >
                Rejected: "{entry.rejection_reason}"
            </Text>
        </View>
    )
}

function DesktopEntryRow({
    entry,
    onReject,
    onApprove,
    onEdit,
    onTravelTap,
    isAdmin,
    showEdit,
    onViewHistory,
    overlapMinutes,
    isLocked,
    onShowEntryInfo,
    isSelectable,
    isSelected,
    onToggleSelect,
}: CompactEntryRowProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const { workHours, wasEdited, isRejected, timeRange, detailText, remainingNotes } =
        useEntryDisplayData(entry)
    const canEdit = shouldShowEditButton(entry, isAdmin, showEdit, isLocked)
    const canViewHistory = shouldShowHistoryButton(entry, onViewHistory)
    const chipPress = getEntryTypeChipPress(entry, isAdmin, onTravelTap, () =>
        onShowEntryInfo(entry)
    )

    return (
        <View>
            <View style={styles.entryRowDesktop}>
                {isSelectable && (
                    <Checkbox.Android
                        status={isSelected ? 'checked' : 'unchecked'}
                        onPress={onToggleSelect}
                    />
                )}
                <StatusDot status={entry.status} />
                <Text
                    variant="bodySmall"
                    style={[styles.timeRange, { color: theme.colors.onSurfaceVariant }]}
                >
                    {timeRange}
                </Text>
                <View style={styles.entryTypeColumn}>
                    <EntryTypeChip type={entry.entry_type} onPress={chipPress} />
                </View>
                <View style={styles.detailText}>
                    <Text
                        variant="bodyMedium"
                        style={{ color: theme.colors.onSurface }}
                        numberOfLines={1}
                    >
                        {detailText}
                    </Text>
                    {remainingNotes ? (
                        <Text
                            variant="bodySmall"
                            style={{ color: theme.colors.onSurfaceVariant, fontStyle: 'italic' }}
                            numberOfLines={1}
                        >
                            {remainingNotes}
                        </Text>
                    ) : null}
                </View>
                {wasEdited && <AdminEditedChip timeEntryId={entry.id} />}
                {isLocked && (
                    <Tooltip title="This entry is in a locked period">
                        <View>
                            <Icon source="lock" size={16} color={theme.colors.onSurfaceVariant} />
                        </View>
                    </Tooltip>
                )}
                {overlapMinutes != null && (
                    <Tooltip title={formatOverlapTooltip(overlapMinutes)}>
                        <View>
                            <Icon
                                source="swap-horizontal"
                                size={16}
                                color={semanticColors.warning}
                            />
                        </View>
                    </Tooltip>
                )}
                <Text
                    variant="bodyMedium"
                    style={{
                        fontWeight: '600',
                        color: isRejected ? theme.colors.onSurfaceVariant : theme.colors.primary,
                        minWidth: 44,
                        textAlign: 'right',
                        textDecorationLine: isRejected ? 'line-through' : 'none',
                    }}
                >
                    {formatHoursReadable(workHours)}
                </Text>
                <EntryActionButtons
                    entry={entry}
                    canEdit={canEdit}
                    canViewHistory={canViewHistory}
                    onEdit={onEdit}
                    onReject={onReject}
                    onApprove={onApprove}
                    onViewHistory={onViewHistory}
                />
            </View>
            <RejectionReasonRow entry={entry} />
        </View>
    )
}

function EntryActionButtons({
    entry,
    canEdit,
    canViewHistory,
    onEdit,
    onReject,
    onApprove,
    onViewHistory,
}: {
    entry: TimeEntriesExpanded
    canEdit: boolean
    canViewHistory: boolean | string
    onEdit: () => void
    onReject?: () => void
    onApprove?: () => void
    onViewHistory?: (entry: TimeEntriesExpanded) => void
}) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()

    return (
        <View style={styles.entryActions}>
            {canViewHistory && (
                <IconButton
                    icon="history"
                    size={18}
                    onPress={() => onViewHistory?.(entry)}
                    iconColor={theme.colors.tertiary}
                    style={styles.iconBtn}
                />
            )}
            {canEdit && (
                <IconButton icon="pencil" size={18} onPress={onEdit} style={styles.iconBtn} />
            )}
            {onApprove && (
                <IconButton
                    icon="check"
                    size={18}
                    onPress={onApprove}
                    iconColor={semanticColors.success}
                    style={styles.iconBtn}
                />
            )}
            {onReject && !onApprove && (
                <IconButton
                    icon="close"
                    size={18}
                    onPress={onReject}
                    iconColor={semanticColors.error}
                    style={styles.iconBtn}
                />
            )}
        </View>
    )
}

function MobileEntryRow({
    entry,
    onReject,
    onApprove,
    onEdit,
    onTravelTap,
    isAdmin,
    showEdit,
    onViewHistory,
    overlapMinutes,
    isLocked,
    onShowEntryInfo,
    isSelectable,
    isSelected,
    onToggleSelect,
}: CompactEntryRowProps) {
    const theme = useTheme()
    const semanticColors = useSemanticColors()
    const { workHours, wasEdited, isRejected, timeRange, detailText, remainingNotes } =
        useEntryDisplayData(entry)
    const canEdit = shouldShowEditButton(entry, isAdmin, showEdit, isLocked)
    const canViewHistory = shouldShowHistoryButton(entry, onViewHistory)
    const chipPress = getEntryTypeChipPress(entry, isAdmin, onTravelTap, () =>
        onShowEntryInfo(entry)
    )

    return (
        <View style={styles.entryRowMobile}>
            <View style={styles.mobileTopRow}>
                {isSelectable && (
                    <Checkbox.Android
                        status={isSelected ? 'checked' : 'unchecked'}
                        onPress={onToggleSelect}
                    />
                )}
                <StatusDot status={entry.status} />
                <Text variant="bodySmall" style={{ color: theme.colors.onSurfaceVariant }}>
                    {timeRange}
                </Text>
                <View style={{ flex: 1 }} />
                <Text
                    variant="bodyMedium"
                    style={{
                        fontWeight: '600',
                        color: isRejected ? theme.colors.onSurfaceVariant : theme.colors.primary,
                        textDecorationLine: isRejected ? 'line-through' : 'none',
                    }}
                >
                    {formatHoursReadable(workHours)}
                </Text>
            </View>
            <View style={styles.mobileDetailRow}>
                <EntryTypeChip type={entry.entry_type} onPress={chipPress} iconOnly />
                {detailText ? (
                    <Text
                        variant="bodySmall"
                        style={{ color: theme.colors.onSurfaceVariant, flex: 1 }}
                        numberOfLines={1}
                    >
                        {detailText}
                    </Text>
                ) : (
                    <View style={{ flex: 1 }} />
                )}
                {wasEdited && <AdminEditedChip timeEntryId={entry.id} />}
                {isLocked && <Icon source="lock" size={14} color={theme.colors.onSurfaceVariant} />}
                {overlapMinutes != null && (
                    <Icon source="swap-horizontal" size={14} color={semanticColors.warning} />
                )}
                <EntryActionButtons
                    entry={entry}
                    canEdit={canEdit}
                    canViewHistory={canViewHistory}
                    onEdit={onEdit}
                    onReject={onReject}
                    onApprove={onApprove}
                    onViewHistory={onViewHistory}
                />
            </View>
            {remainingNotes ? (
                <View style={styles.notesRow}>
                    <Icon source="note-text" size={14} color={theme.colors.onSurfaceVariant} />
                    <Text
                        variant="bodySmall"
                        style={{
                            color: theme.colors.onSurfaceVariant,
                            fontStyle: 'italic',
                            flex: 1,
                        }}
                        numberOfLines={2}
                    >
                        {remainingNotes}
                    </Text>
                </View>
            ) : null}
            <RejectionReasonRow entry={entry} />
        </View>
    )
}

const styles = StyleSheet.create({
    dayGroup: {
        marginVertical: 4,
    },
    header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: 16,
        paddingVertical: 12,
        borderBottomWidth: 1,
    },
    headerLeft: {
        flex: 1,
    },
    headerRight: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
    },
    userRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    dateLabelRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    footer: {
        flexDirection: 'row',
        justifyContent: 'flex-end',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        borderTopWidth: 1,
        gap: 12,
    },
    footerTotal: {
        alignItems: 'flex-end',
    },
    approveLabel: {
        fontSize: 12,
    },
    entryRowDesktop: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingHorizontal: 16,
        paddingVertical: 10,
        gap: 12,
    },
    timeRange: {
        width: 140,
        fontVariant: ['tabular-nums'],
    },
    checkboxSpacer: {
        width: 40,
    },
    entryTypeColumn: {
        width: 110,
    },
    detailText: {
        flex: 1,
    },
    entryActions: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-end',
        marginLeft: 4,
        minWidth: 72,
    },
    iconBtn: {
        margin: 0,
    },
    entryRowMobile: {
        paddingHorizontal: 16,
        paddingVertical: 10,
        gap: 4,
    },
    mobileTopRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 8,
    },
    mobileDetailRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    notesRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 4,
    },
    rejectionRow: {
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: 4,
        paddingHorizontal: 16,
        paddingBottom: 6,
    },
})
